/*
 * SORTING THE PAGE BOXES (Chris, 2026-10-24: "a manual sort page slot boxes option AND an auto sort as playback goes on"). The pure rules, so they are tested without a browser.
 *
 * The homepage keeps slot 0, and a locked page (restricted or disabled) keeps its place; every other page in a box is sorted by its page views, and the sorted pages fill the same set of boxes in
 * order (first box first). The server accepts a reorder only as a MOVE (one page to an empty box) or a SWAP (two pages exchanging boxes), so reaching a sorted layout is a list of such steps, each valid on its own.
 */
import { HOME_SLOT, MAX_PAGE_SLOT, SlotChange, SlotPage } from "./slotReorder";

export type SortOrder = "desc" | "asc";

const movable = (p: SlotPage): boolean => !!p?.selected && typeof p.slotIndex === "number" && p.slotIndex > HOME_SLOT && p.slotIndex <= MAX_PAGE_SLOT && !p.restricted && !p.disabled;

/**
 * Where each movable page should be. `margin` is how far apart two pages' views must be before one is put ahead of the other: with 0 the order is strict, with more a page keeps its place against
 * a neighbour it is nearly level with (so an auto sort does not shuffle two pages that trade the lead every few seconds). Ties keep the current order.
 */
export const sortTarget = (pages: SlotPage[], views: Map<string, number>, order: SortOrder, margin = 0): Map<string, number> => {
  const items = (pages || []).filter(movable).sort((a, b) => (a.slotIndex as number) - (b.slotIndex as number));
  const slots = items.map((p) => p.slotIndex as number);
  const v = (p: SlotPage) => views.get(p.name) || 0;
  const ahead = (a: SlotPage, b: SlotPage) => (order === "desc" ? v(a) - v(b) : v(b) - v(a)) > Math.max(0, margin);   // a clearly belongs ahead of b
  // bubble the clear differences into place; a page level with its neighbour stays where it is
  for (let pass = 0; pass < items.length; pass++) {
    let moved = false;
    for (let i = 0; i < items.length - 1; i++) {
      if (ahead(items[i + 1], items[i])) {
        [items[i], items[i + 1]] = [items[i + 1], items[i]];
        moved = true;
      }
    }
    if (!moved) break;
  }

  return new Map(items.map((p, i) => [p.name, slots[i]]));
};

/** the steps (each one move or one swap, to be applied in order) that take the pages from where they are to `target`; nothing for a page already there */
export const stepsToReach = (pages: SlotPage[], target: Map<string, number>): SlotChange[][] => {
  const at = new Map<string, number>();
  (pages || []).filter(movable).forEach((p) => at.set(p.name, p.slotIndex as number));
  const owner = new Map<number, string>();
  at.forEach((slot, name) => owner.set(slot, name));
  const wanted = new Map<number, string>();
  target.forEach((slot, name) => { if (at.has(name)) wanted.set(slot, name); });
  const steps: SlotChange[][] = [];
  const slots = Array.from(wanted.keys()).sort((a, b) => a - b);
  for (const slot of slots) {
    const want = wanted.get(slot) as string;
    const have = owner.get(slot);
    if (have === want) continue;
    const from = at.get(want) as number;
    if (have === undefined) {
      steps.push([{ pageName: want, fromSlot: from, toSlot: slot }]);
      owner.delete(from);
    } else {
      steps.push([{ pageName: want, fromSlot: from, toSlot: slot }, { pageName: have, fromSlot: slot, toSlot: from }]);
      owner.set(from, have);
      at.set(have, from);
    }
    owner.set(slot, want);
    at.set(want, slot);
  }

  return steps;
};
